import { useState, type ReactNode } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Skeleton } from "primereact/skeleton";
import { SelectionSummary } from "./SelectionSummary";
import { formatDuration } from "../../atlas/utils";

export interface SelectableTableColumn<T> {
  field: string;
  header: string;
  body?: (row: T) => ReactNode;
}

export interface SelectableTableProps<T extends Record<string, any>> {
  items?: T[];
  selected?: T[];
  columns?: SelectableTableColumn<T>[];
  getKey: (item: T) => string | number;
  onChange: (selected: T[]) => void;
  readOnly?: boolean;
  isLoading?: boolean;
  showSelectionSummary?: boolean;
  getDuration?: (item: T) => number | undefined;
  emptyMessage?: string | undefined;
  "aria-label"?: string;
}

export function SelectableTable<T extends Record<string, any>>({
  items = [],
  selected = [],
  columns = [],
  getKey,
  onChange,
  readOnly = false,
  isLoading = false,
  showSelectionSummary = false,
  getDuration = item => item.duration,
  emptyMessage,
  "aria-label": ariaLabel = "Selectable items",
}: SelectableTableProps<T>) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [toAdd, setToAdd] = useState<T[]>([]);
  const [toRemove, setToRemove] = useState<T[]>([]);

  const selectedKeys = selected.map(getKey);

  // Create skeleton data for loading state
  const skeletonData = Array.from({ length: 10 }, (_, i) => ({ id: `skeleton-${i}` }));
  const skeletonBodyTemplate = () => <Skeleton />;

  const availableItems = items.filter(item => !selectedKeys.includes(getKey(item)));
  const lessonCount = selected.length;
  const durationMinutes = selected.reduce((sum, item) => sum + (Number(getDuration(item)) || 0), 0);
  const formattedDuration = formatDuration(durationMinutes);

  const handleAdd = () => {
    onChange([...selected, ...toAdd]);
    setToAdd([]);
  };

  const handleRemove = () => {
    onChange(selected.filter(item => !toRemove.some(r => getKey(r) === getKey(item))));
    setToRemove([]);
  };

  if (readOnly) {
    return (
      <>
        {showSelectionSummary && (
          <div className="text-right">
            <SelectionSummary durationMinutes={formattedDuration} lessonCount={lessonCount} />
          </div>
        )}
        <div className="flex items-center mb-3 gap-5">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              placeholder="Search..."
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </IconField>
        </div>
        <DataTable<T[]>
          value={selected}
          dataKey={row => String(getKey(row))}
          paginator
          rows={10}
          globalFilter={globalFilter}
          globalFilterFields={columns.map(col => col.field)}
          aria-label={ariaLabel}
          emptyMessage="No items found."
        >
          {columns.map(col => (
            <Column key={col.field} field={col.field} header={col.header} body={col.body} />
          ))}
        </DataTable>
      </>
    );
  }

  return (
    <>
      {showSelectionSummary && (
        <div className="text-right">
          <SelectionSummary durationMinutes={formattedDuration} lessonCount={lessonCount} />
        </div>
      )}
      <TabView aria-label="Item selection">
        {/* Available */}
        <TabPanel header="Available">
          <div className="flex items-center mb-3 gap-5">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                placeholder="Search..."
                value={globalFilter}
                onChange={e => setGlobalFilter(e.target.value)}
              />
            </IconField>
            <Button
              label={`Add ${toAdd.length}`}
              onClick={handleAdd}
              disabled={toAdd.length === 0 || isLoading}
            />
          </div>
          <DataTable<T[]>
            value={(isLoading ? skeletonData : availableItems) as T[]}
            dataKey={row => String(isLoading ? row.id : getKey(row))}
            selection={isLoading ? [] : toAdd}
            onSelectionChange={e => !isLoading && setToAdd(e.value)}
            selectionMode="multiple"
            paginator
            rows={10}
            globalFilter={globalFilter}
            globalFilterFields={columns.map(col => col.field)}
            aria-label={`Available ${ariaLabel}`}
            emptyMessage={emptyMessage || "No available items."}
          >
            <Column
              selectionMode="multiple"
              style={{ width: "3rem" }}
              body={isLoading ? skeletonBodyTemplate : undefined}
            />
            {columns.map(col => (
              <Column
                sortable
                key={col.field}
                field={col.field}
                header={col.header}
                body={isLoading ? skeletonBodyTemplate : col.body}
              />
            ))}
          </DataTable>
        </TabPanel>

        {/* Added */}
        <TabPanel header={`Added (${selected.length})`}>
          <div className="flex items-center mb-3 gap-5">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                placeholder="Search..."
                value={globalFilter}
                onChange={e => setGlobalFilter(e.target.value)}
              />
            </IconField>
            <Button
              label={`Remove ${toRemove.length}`}
              onClick={handleRemove}
              disabled={toRemove.length === 0 || isLoading}
            />
          </div>
          <DataTable<T[]>
            value={(isLoading ? skeletonData : selected) as T[]}
            dataKey={row => String(isLoading ? row.id : getKey(row))}
            reorderableRows={!isLoading}
            onRowReorder={e => {
              !isLoading && onChange(e.value);
            }}
            selection={isLoading ? [] : toRemove}
            onSelectionChange={e => !isLoading && setToRemove(e.value)}
            selectionMode="multiple"
            paginator
            rows={10}
            globalFilter={globalFilter}
            globalFilterFields={columns.map(col => col.field)}
            aria-label={`Added ${ariaLabel}`}
            emptyMessage="No items added yet."
          >
            <Column
              rowReorder
              style={{ width: "3rem" }}
              body={isLoading ? skeletonBodyTemplate : undefined}
            />
            <Column
              selectionMode="multiple"
              style={{ width: "3rem" }}
              body={isLoading ? skeletonBodyTemplate : undefined}
            />
            {columns.map(col => (
              <Column
                sortable
                key={col.field}
                field={col.field}
                header={col.header}
                body={isLoading ? skeletonBodyTemplate : col.body}
              />
            ))}
          </DataTable>
        </TabPanel>
      </TabView>
    </>
  );
}
